import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spin, Button, Modal, Table } from 'antd';
import { AppState } from 'store';
import {
  FormField,
  defaultDateFormat,
  showSuccessNotification,
  scrollTop,
  CommonTable,
  Date as DateComponent,
} from 'common';
import { fetchHistoryAsync, searchHistoryClear } from './SearchHistory.actions';
import { UserSearchHistory } from '.';
import moment from 'moment';
import {
  Tabs,
  SearchTypes,
  NYC_DB_FORM_NAME,
  OtherTabFieldNames,
  initialAliases,
  initialCorporations,
  initialAddresses,
} from '../SearchForm/consts';
import styles from './SearchHistory.module.scss';
import { getFormValues } from 'redux-form';
import { createSelector } from 'reselect';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { getAccessToken } from 'common/helpers';
import { InteractionStatus } from '@azure/msal-browser';

const { Column } = Table;

const selector = createSelector(
  (state: AppState) => state.login,
  (state: AppState) => state.searchHistory,
  (state: AppState) => getFormValues(NYC_DB_FORM_NAME)(state),
  (login, searchHistory, formValues: any) => ({
    login,
    searchHistory,
    formValues,
  }),
);

const SearchHistory: React.FC<{ changeFormValues: any }> = ({
  changeFormValues,
}) => {
  const dispatch = useDispatch();
  const { login, searchHistory, formValues } = useSelector(selector);
  const [modal, setModal] = useState(false);
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts[0] || {});

  useEffect(() => {
    if (account && inProgress === InteractionStatus.None && modal) {
      getAccessToken(instance, account, inProgress).then((response) => {
        if (response?.accessToken)
          dispatch(
            fetchHistoryAsync(formValues.searchName, response.accessToken),
          );
      });
    }
  }, [dispatch, instance, account, inProgress, modal, formValues, login]);

  const setParams = (item: UserSearchHistory) => {
    if (isAuthenticated) {
      const { searchParameters, searchType, createDate } = item;
      const {
        from,
        to,
        bookType,
        hob,
        documentTypes,
        searchBy,
        searchByIndividual,
        searchByCorporation,
        aliases,
        corporations,
        block,
        lot,
        ease,
        street,
        houseNumber,
        apartmentNumber,
        ownerName,
        acris,
        controlNumber,
        crfn,
        documentId,
        fileNumber,
        page,
        reel,
        documentTypesByBlockLot,
        documentTypesByName,
        documentTypesByAddress,
        searchByBlockLot,
        searchByName,
        searchByAddress,
        addresses,
      } = searchParameters || {};

      changeFormValues('hob', hob);

      if (searchType !== SearchTypes.Other) {
        let newFromDate: Date;

        if (to && moment(createDate).isAfter(to)) {
          // Create Date is after To Date, therefore set From Date to value of To Date
          newFromDate = to;
        } else {
          // Create Date is before To Date or To Date doesn't exist, therefore set From Date to value of Create Date
          newFromDate = createDate;
        }

        changeFormValues('from', moment(newFromDate).format(defaultDateFormat));

        // set To Date to today
        changeFormValues('to', moment(new Date()).format(defaultDateFormat));

        if (bookType) {
          changeFormValues('bookType', bookType);
        } else {
          changeFormValues('bookType', '');
        }
      } else {
        changeFormValues('from', '');
        changeFormValues('to', '');
        changeFormValues('bookType', '');
      }

      switch (searchType) {
        case SearchTypes.Property:
          changeFormValues('tab', Tabs.Property);
          changeFormValues('propertyTab.searchBy', searchBy);

          changeFormValues(
            'propertyTab.assessment',
            documentTypes.indexOf('assessment') !== -1,
          );
          changeFormValues(
            'propertyTab.filedLiens',
            documentTypes.indexOf('liens') !== -1,
          );
          changeFormValues(
            'propertyTab.recordedDocuments',
            documentTypes.indexOf('recordedDocuments') !== -1,
          );

          switch (searchBy) {
            case 'parcel':
              changeFormValues('propertyTab.searchBy', 'parcel');
              changeFormValues('propertyTab.block', block);
              changeFormValues('propertyTab.lot', lot);
              changeFormValues('propertyTab.ease', ease);
              break;
            case 'address':
              changeFormValues('propertyTab.street', street);
              changeFormValues('propertyTab.houseNumber', houseNumber);
              changeFormValues('propertyTab.apartmentNumber', apartmentNumber);
              break;
            case 'assessedOwner':
              changeFormValues('propertyTab.ownerName', ownerName);
              break;
            default:
              break;
          }
          break;
        case SearchTypes.Name:
          changeFormValues('tab', Tabs.Name);

          changeFormValues('nameTab.acris', acris);

          changeFormValues(
            'nameTab.judgmentsLiens',
            documentTypes.indexOf('liens') !== -1,
          );
          changeFormValues(
            'nameTab.ftl',
            documentTypes.indexOf('federalTaxLiens') !== -1,
          );
          changeFormValues(
            'nameTab.parkingViolations',
            documentTypes.indexOf('parkingViolationJudgments') !== -1,
          );
          changeFormValues(
            'nameTab.ecb',
            documentTypes.indexOf('environmentalControlBoardJudgments') !== -1,
          );
          changeFormValues(
            'nameTab.tab',
            documentTypes.indexOf('transitAdjudicationBureauJudgments') !== -1,
          );
          changeFormValues(
            'nameTab.ucc',
            documentTypes.indexOf('uniformCommercialCodes') !== -1,
          );
          changeFormValues(
            'nameTab.recordedDocuments',
            documentTypes.indexOf('recordedDocuments') !== -1,
          );

          if (aliases?.length > 0) {
            changeFormValues('nameTab.aliases', aliases);
          } else {
            changeFormValues('nameTab.aliases', initialAliases);
          }

          if (corporations?.length > 0) {
            changeFormValues('nameTab.corporations', corporations);
          } else {
            changeFormValues('nameTab.corporations', initialCorporations);
          }

          changeFormValues('nameTab.searchByIndividual', searchByIndividual);
          changeFormValues('nameTab.searchByCorporation', searchByCorporation);

          // this is to accomodate legacy contins that were using searchBy before we implemented the ability to search by both individual and corporation at the same time
          if (!searchByIndividual && !searchByCorporation) {
            if (searchBy === 'individual')
              changeFormValues('nameTab.searchByIndividual', true);
            else if (searchBy === 'corporation')
              changeFormValues('nameTab.searchByCorporation', true);
          }
          break;
        case SearchTypes.Other:
          changeFormValues('tab', Tabs.Other);
          changeFormValues(OtherTabFieldNames.SEARCHBY, searchBy);
          changeFormValues(OtherTabFieldNames.DOCUMENT_ID, documentId);
          changeFormValues(OtherTabFieldNames.CRFN, crfn);
          changeFormValues(OtherTabFieldNames.REEL, reel);
          changeFormValues(OtherTabFieldNames.PAGE, page);
          changeFormValues(OtherTabFieldNames.FILE_NUMBER, fileNumber);
          changeFormValues(OtherTabFieldNames.CONTROL_NUMBER, controlNumber);

          if (addresses?.length > 0) {
            changeFormValues(OtherTabFieldNames.ADDRESSES, addresses);
          } else {
            changeFormValues(OtherTabFieldNames.ADDRESSES, initialAddresses);
          }
          break;
        case SearchTypes.Multi:
          changeFormValues('tab', Tabs.Multi);

          // block lot search fields
          changeFormValues('multiTab.searchByBlockLot', searchByBlockLot);
          changeFormValues(
            'multiTab.assessment',
            documentTypesByBlockLot.indexOf('assessment') !== -1,
          );
          changeFormValues(
            'multiTab.filedLiens',
            documentTypesByBlockLot.indexOf('liens') !== -1,
          );
          changeFormValues(
            'multiTab.recordedDocumentsByBlockLot',
            documentTypesByBlockLot.indexOf('recordedDocuments') !== -1,
          );

          changeFormValues('multiTab.block', block);
          changeFormValues('multiTab.lot', lot);
          changeFormValues('multiTab.ease', ease);

          // name search fields
          changeFormValues('multiTab.searchByName', searchByName);
          changeFormValues('multiTab.acris', acris);

          changeFormValues(
            'multiTab.judgmentsLiens',
            documentTypesByName.indexOf('liens') !== -1,
          );
          changeFormValues(
            'multiTab.ftl',
            documentTypesByName.indexOf('federalTaxLiens') !== -1,
          );
          changeFormValues(
            'multiTab.parkingViolations',
            documentTypesByName.indexOf('parkingViolationJudgments') !== -1,
          );
          changeFormValues(
            'multiTab.ecb',
            documentTypesByName.indexOf(
              'environmentalControlBoardJudgments',
            ) !== -1,
          );
          changeFormValues(
            'multiTab.tab',
            documentTypesByName.indexOf(
              'transitAdjudicationBureauJudgments',
            ) !== -1,
          );
          changeFormValues(
            'multiTab.ucc',
            documentTypesByName.indexOf('uniformCommercialCodes') !== -1,
          );
          changeFormValues(
            'multiTab.recordedDocuments',
            documentTypesByName.indexOf('recordedDocuments') !== -1,
          );

          if (aliases?.length > 0) {
            changeFormValues('multiTab.aliases', aliases);
          } else {
            changeFormValues('multiTab.aliases', initialAliases);
          }

          if (corporations?.length > 0) {
            changeFormValues('multiTab.corporations', corporations);
          } else {
            changeFormValues('multiTab.corporations', initialCorporations);
          }

          changeFormValues('multiTab.searchByIndividual', searchByIndividual);
          changeFormValues('multiTab.searchByCorporation', searchByCorporation);

          // address search fields
          changeFormValues('multiTab.searchByAddress', searchByAddress);
          changeFormValues(
            'multiTab.ecbsByAddress',
            documentTypesByAddress.indexOf(
              'environmentalControlBoardJudgments',
            ) !== -1,
          );
          changeFormValues(
            'multiTab.emergencyRepairs',
            documentTypesByAddress.indexOf('emergencyRepairs') !== -1,
          );

          if (addresses?.length > 0) {
            changeFormValues('multiTab.addresses', addresses);
          } else {
            changeFormValues('multiTab.addresses', initialAddresses);
          }
          break;
        default:
          break;
      }

      setModal(false);
      showSuccessNotification(
        `Title Number/Reference`,
        `Fulfilled by "${item.searchName}"`,
      );
      scrollTop();
    }

    changeFormValues('searchName', item.searchName);
  };

  const categoryNames: Record<string, string> = {};
  categoryNames[SearchTypes.Property] = 'Property Search';
  categoryNames[SearchTypes.Name] = 'Name Search';
  categoryNames[SearchTypes.Other] = 'Other Search';
  categoryNames[SearchTypes.Multi] = 'Multi Search';

  return (
    <>
      <Modal
        title={`FOUND ${searchHistory.history.length} HITS`}
        footer={null}
        visible={modal}
        onCancel={() => setModal(false)}
        width={768}
      >
        <Spin spinning={searchHistory.loading} tip="Searching Title Number...">
          <CommonTable items={searchHistory.history} rowKey="searchId">
            <Column title="Title Number" dataIndex="searchName" />
            <Column
              title="Category"
              render={(item: UserSearchHistory) => {
                return categoryNames[item.searchType];
              }}
            />
            <Column
              title="Run Date"
              render={(item: UserSearchHistory) => {
                return (
                  <DateComponent type="date">{item.createDate}</DateComponent>
                );
              }}
            />
            <Column
              title="Run Time"
              render={(item: UserSearchHistory) => {
                return (
                  <DateComponent type="time">{item.createDate}</DateComponent>
                );
              }}
            />
            <Column
              title="Action"
              render={(text: string, record: any) => (
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setParams(record);
                    dispatch(searchHistoryClear());
                  }}
                >
                  Run Contin
                </Button>
              )}
            />
          </CommonTable>
        </Spin>
      </Modal>

      <FormField
        className={'m-0 ' + styles.autoComplete}
        name="searchName"
        placeholder="Title Number/Reference"
        allowClear={false}
        addonAfter={
          <Button
            disabled={formValues.searchName === ''}
            type="link"
            size="small"
            onClick={() => {
              setModal(true);
            }}
          >
            Contin
          </Button>
        }
      />
    </>
  );
};

export default SearchHistory;
